import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Chip } from '@mui/joy';

import {
  CHAT_MODEL_MAPPING,
  getModelDisplayNameFromIdList,
} from '@/Pages/Playground/Playground.constants';
import { cn } from '@/utils/tailwind';

import { InstructModelBadge } from '../../InstructModelBadge';
import {
  ManifestEditorProps,
  ManifestPreviewProps,
  tableVariantContainerClass,
} from '../../Manifest.utils';
import { ManifestNavigation } from '../../ManifestNavigation';
import { ManifestSelectModel } from '../../ManifestSelectModel';
import { ManifestProviderReplaceWithSwitch } from '../../ManifestSelectProvider';
import { ManifestSlider } from '../../ManifestSlider';
import { DEFAULT_MODEL, MODELS } from './ManifestGoogleVertexAI.utils';

export function ManifestGoogleVertexAI(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const kwargs = props.value.kwargs;

  return (
    <>
      <ManifestNavigation navigation={props.navigation} />

      <div
        className={cn(
          'flex flex-col gap-3',
          props.variant === 'table' && tableVariantContainerClass
        )}
      >
        <ManifestProviderReplaceWithSwitch
          variant={props.variant}
          onChange={props.onChange}
          value={props.value}
          readOnly={props.readOnly}
          isChatModel={false}
          otherModelId={CHAT_MODEL_MAPPING['ChatVertexAI'].modelId}
        />

        <ManifestSelectModel
          models={MODELS}
          onChange={(model) => {
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, model },
            });
          }}
          value={kwargs.model ?? DEFAULT_MODEL}
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Temperature"
          value={kwargs.temperature}
          min={0}
          max={1}
          step={0.1}
          onChange={(temperature) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, temperature },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Max Output Tokens"
          value={kwargs.max_output_tokens}
          min={0}
          max={2048}
          step={1}
          onChange={(max_output_tokens) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, max_output_tokens },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Top K"
          value={kwargs.top_k}
          min={1}
          max={40}
          step={0.01}
          onChange={(top_k) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, top_k },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Top P"
          value={kwargs.top_p}
          min={0}
          max={1}
          step={0.1}
          onChange={(top_p) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, top_p },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />
      </div>
    </>
  );
}

export function ManifestGoogleVertexAIPreview(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const displayLabel = getModelDisplayNameFromIdList(props.value.id);
  const kwargs = props.value.kwargs;
  const model = kwargs.model ?? DEFAULT_MODEL;

  if (props.variant === 'line') {
    return (
      <div className="flex flex-row items-center gap-1">
        <span className="font-semibold">{displayLabel}</span>
        <span>{model}</span>
        <InstructModelBadge />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        'flex w-auto select-none flex-col rounded-md bg-neutral-50 p-4 shadow-sm dark:bg-[rgb(9,9,13)]',
        props.onClick ? 'cursor-pointer' : 'cursor-default'
      )}
    >
      <span className="flex items-center gap-2">
        <span className="font-semibold">{displayLabel}</span>
      </span>

      <span className="mt-3 flex items-center gap-1">
        <Chip variant="outlined" size="sm" color="neutral">
          {model}
        </Chip>

        {kwargs.temperature != null && (
          <Chip variant="outlined" size="sm" color="neutral">
            Temperature: {kwargs.temperature}
          </Chip>
        )}

        {kwargs.max_output_tokens != null && (
          <Chip variant="outlined" size="sm" color="neutral">
            Max Output Tokens: {kwargs.max_output_tokens}
          </Chip>
        )}
      </span>
    </button>
  );
}
